import React, { useState, useEffect, ChangeEvent } from "react";
import { useToastStore } from "store/toastStore";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { getFeedbackTypes, submitFeedback, FeedbackType } from "apis/settingsServices";
import Select, { GroupBase, StylesConfig } from "react-select";

const ContactUs: React.FC = () => {
  const { showToast } = useToastStore();
  const { showLoader, hideLoader } = useGlobalLoader();

  const [feedbackType, setFeedbackType] = useState<number | string>("");
  const [feedbackTypes, setFeedbackTypes] = useState<FeedbackType[]>([]);
  const [description, setDescription] = useState<string>("");
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ feedbackType?: string; description?: string }>({});
  const [loadingTypes, setLoadingTypes] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [descriptionLengthError, setDescriptionLengthError] = useState<string>("");
  interface FeedbackOption {
    value: number;
    label: string;
  }

  const customStyles: StylesConfig<FeedbackOption, false, GroupBase<FeedbackOption>> = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "#F5F9FE",
      border: state.isFocused ? "2px solid #DCEEFF" : "none",
      boxShadow: state.isFocused ? "0 0 0 1px #DCEEFF" : "none",
      borderRadius: "8px",
      minHeight: "50px",
      height: "fit-content",
      padding: "2px 6px",
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        backgroundColor: "#EAF4FF",
      },
    }),

    menu: (provided) => ({
      ...provided,
      backgroundColor: "#F5F9FE",
      borderRadius: "8px",
      marginTop: "4px",
      boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
    }),

    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#DCEEFF" : state.isFocused ? "#EAF4FF" : "#F5F9FE",
      color: "#000",
      fontSize: "14px",
      fontWeight: 500,
      padding: "10px 14px",
      cursor: "pointer",
      transition: "background-color 0.15s ease-in-out",
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: "14px",
      color: "#8C8C8C",
    }),
  };

  // Fetch feedback types on component mount
  useEffect(() => {
    const fetchFeedbackTypes = async () => {
      try {
        setLoadingTypes(true);
        const types = await getFeedbackTypes();
        setFeedbackTypes(types);
      } catch (error) {
        console.error("Error fetching feedback types:", error);
        showToast("Failed to load feedback types", "error");
      } finally {
        setLoadingTypes(false);
      }
    };

    fetchFeedbackTypes();
  }, [showToast]);
  const feedbackOptions: FeedbackOption[] = feedbackTypes.map((type) => ({
    value: type.id,
    label: type.typeName,
  }));

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type (only PNG and JPEG/JPG)
      const allowedTypes = ["image/png", "image/jpeg"];
      if (!allowedTypes.includes(file.type)) {
        showToast("Please select a valid image file (PNG or JPG only)", "error");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showToast("Image size should be less than 5MB", "error");
        return;
      }

      setSelectedImage(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const validateForm = (): boolean => {
    const newErrors: { feedbackType?: string; description?: string } = {};

    if (!feedbackType) {
      newErrors.feedbackType = "Please select a feedback type";
    }

    if (!description.trim()) {
      newErrors.description = "Please provide a description";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      showLoader();
      setIsSubmitting(true);

      // Create FormData for multipart/form-data request
      const formData = new FormData();
      formData.append("FeedbackComment", description);
      formData.append("FeedbackTypeId", String(feedbackType));
      if (selectedImage) {
        formData.append("Image", selectedImage);
      }

      // Submit feedback via API
      await submitFeedback(formData);

      showToast("Feedback submitted successfully", "success");

      // Clear form
      setFeedbackType("");
      setDescription("");
      setSelectedImage(null);
      setImagePreview(null);
      setErrors({});
      setDescriptionLengthError("");
    } catch (error) {
      console.error("Error submitting feedback:", error);
      showToast("Failed to submit feedback. Please try again.", "error");
    } finally {
      hideLoader();
      setIsSubmitting(false);
    }
  };

   const isFormValid = feedbackType && description.trim().length > 0;

  return (
    <div className="mt-4 sm:pl-64 pb-8">
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-grease-100 mb-6">Contact Us</h2>
        <p className="text-sm text-lightgray-200 mb-6">
          We'd love to hear from you. Please share your feedback, questions, or report any issues you're experiencing.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Feedback Type Dropdown */}
          <div>
            <label htmlFor="feedbackType" className="block text-sm font-medium text-grease-100 mb-2">
              What is your feedback about? <span className="text-red-500">*</span>
            </label>
            {/* <select
              id="feedbackType"
              value={feedbackType}
              onChange={(e) => {
                setFeedbackType(e.target.value ? Number(e.target.value) : "");
                if (errors.feedbackType) {
                  setErrors({ ...errors, feedbackType: undefined });
                }
              }}
              className={`w-full p-3 rounded-lg border ${
                errors.feedbackType ? "border-red-500" : "border-lightgray-50"
              } bg-lightblue-100 text-sm text-grease-100 focus:outline-none focus:ring-2 focus:ring-primaryblue-50`}
              disabled={loadingTypes}
            >
              <option value="">
                {loadingTypes ? "Loading..." : "Please select"}
              </option>
              {feedbackTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.typeName}
                </option>
              ))}
            </select> */}
            <Select
              options={feedbackOptions}
              value={feedbackOptions.find((option) => option.value === feedbackType) || null}
              onChange={(selectedOption) => {
                const value = (selectedOption as FeedbackOption | null)?.value ?? "";
                setFeedbackType(value);

                if (errors.feedbackType) {
                  setErrors({ ...errors, feedbackType: undefined });
                }
              }}
              placeholder="Select feedback type..."
              isClearable
              isMulti={false} // single select
              className="basic-single-select"
              classNamePrefix="select"
              styles={customStyles}
            />
            {errors.feedbackType && <p className="mt-1 text-sm text-red-500">{errors.feedbackType}</p>}
          </div>

          {/* Description Textarea */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-grease-100 mb-2">
              Please describe when and how this occurred <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length > 255) {
                  setDescriptionLengthError("Description must be 255 characters or less");
                  return;
                }
                setDescription(value);
                setDescriptionLengthError("");
                if (errors.description) {
                  setErrors({ ...errors, description: undefined });
                }
              }}
              onKeyDown={(e) => {
                const target = e.target as HTMLTextAreaElement;
                if (target.value.length >= 255 && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'].includes(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
                  e.preventDefault();
                  setDescriptionLengthError("Description must be 255 characters or less");
                } else if (target.value.length < 255 && descriptionLengthError) {
                  setDescriptionLengthError("");
                }
              }}
              onPaste={(e) => {
                e.preventDefault();
                const pastedText = e.clipboardData.getData('text');
                const newValue = (description + pastedText).substring(0, 255);
                setDescription(newValue);
                if (newValue.length >= 255 && (description + pastedText).length > 255) {
                  setDescriptionLengthError("Description must be 255 characters or less");
                } else {
                  setDescriptionLengthError("");
                }
                if (errors.description) {
                  setErrors({ ...errors, description: undefined });
                }
              }}
              rows={6}
              placeholder="Please describe your feedback in detail..."
              maxLength={255}
              className={`w-full p-3 rounded-lg border ${
                errors.description || descriptionLengthError ? "border-red-500" : "border-none"
              } bg-lightblue-100 text-sm text-grease-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primaryblue-50 resize-none`}
            />
            <div className="flex justify-between items-center mt-1">
              {errors.description && <p className="text-sm text-red-500">{errors.description}</p>}
              {descriptionLengthError && <p className="text-sm text-red-500">{descriptionLengthError}</p>}
              <p className={`text-xs ml-auto ${description.length >= 255 ? "text-red-500" : "text-lightgray-200"}`}>
                {description.length}/255
              </p>
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-grease-100 mb-2">Attach Screenshot (Optional)</label>

            {!imagePreview ? (
              <div className="relative bg-lightblue-100">
                <input
                  type="file"
                  id="imageUpload"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <label
                  htmlFor="imageUpload"
                  className="flex flex-col items-center justify-center w-full p-6 border-2 border-dashed border-lightgray-50 rounded-lg cursor-pointer hover:border-primaryblue-50 transition-colors"
                >
                  <i className="st-upload text-lightgray-200 text-3xl mb-2"></i>
                  <span className="text-sm text-lightgray-200">Click to upload or drag and drop</span>
                  <span className="text-xs text-lightgray-200 mt-1">PNG or JPG (max. 5MB)</span>
                </label>
              </div>
            ) : (
              <div className="relative inline-block bg-lightblue-100">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-w-full h-auto max-h-64 rounded-lg border border-lightgray-50"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className={`p-4 w-40 rounded-lg text-xs mt-6 text-white bg-[#FFBC00] 
                ${isFormValid && !isSubmitting
                  ? "shadow-button cursor-pointer"
                  : "cursor-not-allowed"}`}
            >
              {isSubmitting ? "SENDING..." : "SUBMIT FEEDBACK"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactUs;
